// Desktop-only boot trace; main.ts copies "[cold-start]" lines into main.log.
// Values are ids, counts and flags only: any other string prints as [redacted].
import { isDesktopApp } from "$lib/utils.js";
import { errorCodeOf } from "$lib/report-failure.js";

export const COLD_START_PREFIX = "[cold-start]";

// ponytail: flat cap so a long session can't flood main.log; raise if a trace is cut short.
const MAX_LINES = 400;

type TraceValue = string | number | boolean | null | undefined;
export type TraceFields = Record<
  string,
  TraceValue | readonly string[] | Readonly<Record<string, number>>
>;

// No '/', '@', '?', '=' or spaces (URLs, emails, queries); JWTs fail the length/eyJ check.
const SAFE_VALUE = /^[\w.:-]{0,64}$/;

function safe(value: string): string {
  return SAFE_VALUE.test(value) && !value.startsWith("eyJ") ? value : "[redacted]";
}

function formatValue(value: TraceFields[string]): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string") return safe(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `[${value.map((v) => safe(String(v))).join(",")}]`;
  const entries = Object.entries(value as Record<string, number>);
  return `{${entries.map(([k, n]) => `${safe(k)}:${Number(n)}`).join(",")}}`;
}

/** An error as its code ("no_daemon") or "unknown": its text can name a person, so it never prints. */
export function errorSlug(err: unknown): string {
  const code = errorCodeOf(err);
  return code && SAFE_VALUE.test(code) ? code : "unknown";
}

export function formatColdStartLine(event: string, fields: TraceFields, ms: number): string {
  const parts = Object.entries(fields).map(([k, v]) => `${safe(k)}=${formatValue(v)}`);
  return [COLD_START_PREFIX, `+${ms}ms`, safe(event), ...parts].join(" ");
}

let lines = 0;

export function coldStartTrace(event: string, fields: TraceFields = {}): void {
  if (lines >= MAX_LINES || !isDesktopApp()) return;
  lines += 1;
  const ms = typeof performance === "undefined" ? 0 : Math.round(performance.now());
  console.info(formatColdStartLine(event, fields, ms));
}

// UI traces (chat-open, composer-disabled, send-ack…) ride the same line and prefix, so
// main.ts copies them too, under their own budget: the boot trace can't starve them.
const MAX_UI_TRACE_LINES = 2000;
let uiLines = 0;

export function uiTrace(event: string, fields: TraceFields = {}): void {
  if (uiLines >= MAX_UI_TRACE_LINES || !isDesktopApp()) return;
  uiLines += 1;
  const ms = typeof performance === "undefined" ? 0 : Math.round(performance.now());
  console.info(formatColdStartLine(event, fields, ms));
}

/** Test seam. */
export function __resetColdStartTrace(): void {
  lines = 0;
  uiLines = 0;
}
